/**
 * The light sculpture at the centre of the hall: three tilted rings turning slowly at
 * different rates, a band of scrolling data marks (a trace, ticks and pulses), an orb of
 * light points that breathes, a small network of nodes orbiting it, and a soft column of
 * light from the dais to the dome. About ten draw calls; all motion is uniforms and
 * transforms, no geometry is rebuilt per frame except the eight network lines.
 */
import * as THREE from 'three';
import { TOKENS } from '../../ui/theme.ts';
import { glowMaterial, radialTexture, seeded, type TimeUniform } from './glow.ts';

export const SCULPTURE = { x: 0, z: 0, centreY: 4.9, daisRadius: 3.4 };

/** The floating parts are built at this scale around the centre. */
const FLOAT_SCALE = 1.25;

export interface Sculpture {
  group: THREE.Group;
  update(dt: number): void;
  /** 0 calm .. 1 bright: the scene raises it as the visitor's journey progresses. */
  setEnergy(energy: number): void;
  /** Tints the inner elements toward a study's colour (undefined returns to cyan). */
  setAccent(colour: string | undefined): void;
}

export function buildSculpture(time: TimeUniform): Sculpture {
  const group = new THREE.Group();
  group.name = 'sculpture';
  group.position.set(SCULPTURE.x, 0, SCULPTURE.z);
  const cy = SCULPTURE.centreY;

  // Dais: a low polished disc with a ring of light at its edge.
  const dais = new THREE.Mesh(
    new THREE.CylinderGeometry(SCULPTURE.daisRadius, SCULPTURE.daisRadius + 0.15, 0.14, 96),
    new THREE.MeshPhongMaterial({ color: '#0d1117', specular: '#3d5a75', shininess: 100 }),
  );
  dais.position.y = 0.07;
  group.add(dais);
  const daisRing = new THREE.Mesh(
    new THREE.TorusGeometry(SCULPTURE.daisRadius + 0.02, 0.022, 6, 160).rotateX(Math.PI / 2),
    glowMaterial(TOKENS.cyan, 0.95),
  );
  daisRing.position.y = 0.145;
  group.add(daisRing);

  // Column of light: an open cylinder fading up and down, with slow rising streaks.
  const columnMaterial = new THREE.ShaderMaterial({
    uniforms: {
      uTime: time,
      uColour: { value: new THREE.Color(TOKENS.cyanSoft) },
      uEnergy: { value: 0.4 },
    },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      uniform vec3 uColour;
      uniform float uEnergy;
      varying vec2 vUv;
      void main() {
        float fade = smoothstep(0.0, 0.08, vUv.y) * (1.0 - smoothstep(0.55, 1.0, vUv.y));
        float streak = pow(0.5 + 0.5 * sin(vUv.x * 6.2831 * 9.0 + sin(vUv.y * 3.0 + uTime * 0.2)), 8.0);
        float rise = 0.5 + 0.5 * sin(vUv.y * 40.0 - uTime * 1.2);
        float a = fade * (0.05 + 0.12 * streak * rise) * (0.6 + uEnergy);
        gl_FragColor = vec4(uColour * a, a);
      }`,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
    // Additive or see-through: one pass, not back faces then front faces.
    forceSinglePass: true,
    toneMapped: false,
  });
  const column = new THREE.Mesh(
    new THREE.CylinderGeometry(0.55, 0.9, 13, 40, 1, true).translate(0, 6.6, 0),
    columnMaterial,
  );
  group.add(column);

  // Rings: outer cyan, inner white, middle blue, each tilted and turning on its own axis.
  const ringSpecs = [
    { r: 2.75, tube: 0.035, colour: TOKENS.cyan, tilt: [0.32, 0, 0.12], speed: 0.09 },
    { r: 2.25, tube: 0.02, colour: TOKENS.blue, tilt: [-0.42, 0, -0.2], speed: -0.13 },
    { r: 1.6, tube: 0.028, colour: TOKENS.white, tilt: [0.15, 0, 0.55], speed: 0.19 },
  ];
  const rings = ringSpecs.map((spec) => {
    const holder = new THREE.Group();
    holder.position.y = cy;
    holder.rotation.set(spec.tilt[0]!, spec.tilt[1]!, spec.tilt[2]!);
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(spec.r, spec.tube, 8, 180).rotateX(Math.PI / 2),
      glowMaterial(spec.colour, 0.95),
    );
    holder.add(ring);
    holder.scale.setScalar(FLOAT_SCALE);
    group.add(holder);
    return { holder, speed: spec.speed, r: spec.r };
  });
  // Bright beads riding the rings: one instanced mesh for all three, placed each frame from
  // the rings' turning frames (one draw call rather than three).
  const BEADS_PER_RING = 4;
  const beads = new THREE.InstancedMesh(
    new THREE.SphereGeometry(0.07, 10, 8),
    glowMaterial('#ffffff', 1),
    rings.length * BEADS_PER_RING,
  );
  beads.frustumCulled = false;
  group.add(beads);
  const beadPoint = new THREE.Vector3();
  const beadDummy = new THREE.Object3D();
  const placeBeads = (): void => {
    rings.forEach((ring, i) => {
      ring.holder.updateMatrix();
      for (let k = 0; k < BEADS_PER_RING; k++) {
        const a = (k / BEADS_PER_RING) * Math.PI * 2 + ring.r;
        beadPoint
          .set(Math.cos(a) * ring.r, 0, Math.sin(a) * ring.r)
          .applyMatrix4(ring.holder.matrix);
        beadDummy.position.copy(beadPoint);
        beadDummy.updateMatrix();
        beads.setMatrixAt(i * BEADS_PER_RING + k, beadDummy.matrix);
      }
    });
    beads.instanceMatrix.needsUpdate = true;
  };

  // Data band: an open ring with scrolling marks, turning against the rings.
  const bandMaterial = new THREE.ShaderMaterial({
    uniforms: {
      uTime: time,
      uColour: { value: new THREE.Color(TOKENS.cyan) },
      uEnergy: { value: 0.4 },
    },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      uniform vec3 uColour;
      uniform float uEnergy;
      varying vec2 vUv;
      float hash(float n) { return fract(sin(n * 91.345) * 47453.21); }
      void main() {
        float u = vUv.x * 120.0 - uTime * 1.5;
        // A trace like a heartbeat across the middle of the band.
        float x = fract(u / 12.0);
        float beat = exp(-pow((x - 0.5) * 18.0, 2.0)) * 0.42 - exp(-pow((x - 0.56) * 22.0, 2.0)) * 0.18;
        float trace = 1.0 - smoothstep(0.0, 0.05, abs(vUv.y - 0.5 - beat));
        // Ticks along the bottom edge, taller every tenth.
        float tick = step(fract(u), 0.08) * step(vUv.y, 0.12 + 0.1 * step(0.9, fract(u / 10.0)));
        // Data cells along the top edge.
        float cell = step(0.55, hash(floor(u * 0.5))) * step(0.84, vUv.y) * step(vUv.y, 0.94)
          * step(0.15, fract(u * 0.5));
        float edge = smoothstep(0.03, 0.0, abs(vUv.y - 0.02)) + smoothstep(0.03, 0.0, abs(vUv.y - 0.98));
        float a = (trace * 0.95 + tick * 0.45 + cell * 0.35 + edge * 0.3) * (0.55 + 0.6 * uEnergy);
        gl_FragColor = vec4(uColour * a, a);
      }`,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
    // Additive or see-through: one pass, not back faces then front faces.
    forceSinglePass: true,
    toneMapped: false,
  });
  const band = new THREE.Mesh(
    new THREE.CylinderGeometry(1.95, 1.95, 0.42, 160, 1, true),
    bandMaterial,
  );
  band.position.y = cy;
  band.rotation.x = 0.06;
  band.scale.setScalar(FLOAT_SCALE);
  group.add(band);

  // Orb: a sphere of points that breathes.
  const rand = seeded(1301);
  const count = 520;
  const positions = new Float32Array(count * 3);
  const phases = new Float32Array(count);
  for (let i = 0; i < count; i++) {
    // Fibonacci sphere with a little jitter.
    const y = 1 - (i / (count - 1)) * 2;
    const rr = Math.sqrt(1 - y * y);
    const theta = i * 2.39996;
    const j = 0.92 + rand() * 0.16;
    positions[i * 3] = Math.cos(theta) * rr * j;
    positions[i * 3 + 1] = y * j;
    positions[i * 3 + 2] = Math.sin(theta) * rr * j;
    phases[i] = rand() * Math.PI * 2;
  }
  const orbGeometry = new THREE.BufferGeometry();
  orbGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  orbGeometry.setAttribute('aPhase', new THREE.BufferAttribute(phases, 1));
  const orbMaterial = new THREE.ShaderMaterial({
    uniforms: {
      uTime: time,
      uMap: { value: radialTexture() },
      uColour: { value: new THREE.Color(TOKENS.cyanSoft) },
      uEnergy: { value: 0.4 },
      uScale: { value: 1 },
    },
    vertexShader: /* glsl */ `
      uniform float uTime;
      uniform float uEnergy;
      attribute float aPhase;
      varying float vGlow;
      void main() {
        float breathe = 0.85 + 0.08 * sin(uTime * 0.8 + aPhase * 0.2);
        vec3 p = position * breathe * (0.9 + 0.1 * uEnergy);
        vec4 mv = modelViewMatrix * vec4(p, 1.0);
        vGlow = 0.45 + 0.55 * (0.5 + 0.5 * sin(uTime * 1.7 + aPhase));
        gl_PointSize = (0.07 + 0.03 * vGlow) * 620.0 / -mv.z;
        gl_Position = projectionMatrix * mv;
      }`,
    fragmentShader: /* glsl */ `
      uniform sampler2D uMap;
      uniform vec3 uColour;
      varying float vGlow;
      void main() {
        float a = texture2D(uMap, gl_PointCoord).a * vGlow;
        gl_FragColor = vec4(uColour * a, a);
      }`,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    toneMapped: false,
  });
  const orb = new THREE.Points(orbGeometry, orbMaterial);
  orb.position.y = cy;
  orb.scale.setScalar(FLOAT_SCALE);
  group.add(orb);
  const halo = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: radialTexture(),
      color: new THREE.Color(TOKENS.cyan).multiplyScalar(0.55),
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      transparent: true,
      toneMapped: false,
    }),
  );
  halo.scale.setScalar(4.2 * FLOAT_SCALE);
  halo.position.y = cy;
  group.add(halo);

  // Network: eight nodes orbiting the orb, each linked to the next and to the centre.
  const nodeCount = 8;
  const nodes = Array.from({ length: nodeCount }, (_, i) => ({
    radius: 1.15 + rand() * 0.3,
    speed: 0.25 + rand() * 0.35,
    phase: (i / nodeCount) * Math.PI * 2,
    tilt: (rand() - 0.5) * 1.2,
  }));
  const nodeMesh = new THREE.InstancedMesh(
    new THREE.SphereGeometry(0.05, 10, 8),
    glowMaterial('#ffffff', 1),
    nodeCount,
  );
  nodeMesh.position.y = cy;
  nodeMesh.scale.setScalar(FLOAT_SCALE);
  group.add(nodeMesh);
  const linkPositions = new Float32Array(nodeCount * 4 * 3);
  const linkGeometry = new THREE.BufferGeometry();
  linkGeometry.setAttribute('position', new THREE.BufferAttribute(linkPositions, 3));
  const links = new THREE.LineSegments(
    linkGeometry,
    new THREE.LineBasicMaterial({
      color: TOKENS.cyan,
      transparent: true,
      opacity: 0.45,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    }),
  );
  links.position.y = cy;
  links.scale.setScalar(FLOAT_SCALE);
  links.frustumCulled = false;
  group.add(links);

  const accentTarget = new THREE.Color(TOKENS.cyan);
  const accentNow = new THREE.Color(TOKENS.cyan);
  let energy = 0.4;
  let energyNow = 0.4;
  const d = new THREE.Object3D();
  const p = new THREE.Vector3();
  const pts: THREE.Vector3[] = nodes.map(() => new THREE.Vector3());

  return {
    group,
    setEnergy: (e) => {
      energy = THREE.MathUtils.clamp(e, 0, 1);
    },
    setAccent: (c) => {
      accentTarget.set(c ?? TOKENS.cyan);
    },
    update: (dt) => {
      const t = time.value;
      energyNow += (energy - energyNow) * Math.min(1, dt * 1.5);
      accentNow.lerp(accentTarget, Math.min(1, dt * 1.2));
      for (const r of rings) r.holder.rotateY(r.speed * dt * (0.7 + energyNow * 0.6));
      placeBeads();
      band.rotation.y -= dt * 0.05;
      orb.rotation.y += dt * 0.08;
      bandMaterial.uniforms.uEnergy!.value = energyNow;
      bandMaterial.uniforms.uColour!.value.copy(accentNow);
      orbMaterial.uniforms.uEnergy!.value = energyNow;
      columnMaterial.uniforms.uEnergy!.value = energyNow;
      (halo.material as THREE.SpriteMaterial).color
        .copy(accentNow)
        .multiplyScalar(0.35 + energyNow * 0.35);
      nodes.forEach((n, i) => {
        const a = n.phase + t * n.speed * 0.4;
        p.set(
          Math.cos(a) * n.radius,
          Math.sin(a * 1.3) * 0.35 + n.tilt * Math.sin(a),
          Math.sin(a) * n.radius,
        );
        pts[i]!.copy(p);
        d.position.copy(p);
        d.updateMatrix();
        nodeMesh.setMatrixAt(i, d.matrix);
      });
      nodeMesh.instanceMatrix.needsUpdate = true;
      let k = 0;
      for (let i = 0; i < nodeCount; i++) {
        const a = pts[i]!;
        const b = pts[(i + 1) % nodeCount]!;
        linkPositions.set([a.x, a.y, a.z, b.x, b.y, b.z], k);
        k += 6;
        linkPositions.set([a.x, a.y, a.z, a.x * 0.35, a.y * 0.35, a.z * 0.35], k);
        k += 6;
      }
      linkGeometry.attributes.position!.needsUpdate = true;
    },
  };
}
